import { useQuery } from '@tanstack/react-query'
import { getApiBaseUrl } from './api'
import {
	CANONICAL_PLAN_IDS,
	PLAN_ENTITLEMENTS,
	type CanonicalPlanId,
} from './entitlements'

const API_URL = getApiBaseUrl()

// Environment variables for price IDs
const legacyMonthlyPriceId = import.meta.env.VITE_STRIPE_MONTHLY_PRICE_ID
const legacyYearlyPriceId = import.meta.env.VITE_STRIPE_YEARLY_PRICE_ID

export const starterMonthlyPriceId =
	import.meta.env.VITE_STRIPE_STARTER_MONTHLY_PRICE_ID || legacyMonthlyPriceId
export const starterYearlyPriceId =
	import.meta.env.VITE_STRIPE_STARTER_YEARLY_PRICE_ID || legacyYearlyPriceId
export const growthMonthlyPriceId =
	import.meta.env.VITE_STRIPE_GROWTH_MONTHLY_PRICE_ID || ''
export const growthYearlyPriceId =
	import.meta.env.VITE_STRIPE_GROWTH_YEARLY_PRICE_ID || ''

// Type for pricing data from API
export interface PricingProduct {
	planId: CanonicalPlanId
	name: string
	description: string | null
	features: string[]
	monthlyPrice: number | null
	yearlyPrice: number | null
	monthlyPriceId: string | null
	yearlyPriceId: string | null
	additionalMemberPriceMonthly: number | null
	additionalMemberPriceYearly: number | null
}

export interface PricingData {
	products: PricingProduct[]
}

// Shared function to get plan features with translations
export const getPlanFeatures = (t: (key: string) => string) => {
	const canonicalPlans = Object.fromEntries(
		CANONICAL_PLAN_IDS.map((planId) => [
			planId,
			PLAN_ENTITLEMENTS[planId].pricingFeatureKeys.map((featureKey) => t(featureKey)),
		])
	) as Record<CanonicalPlanId, string[]>

	return {
		...canonicalPlans,
		enterprise: [
			t('planFeatures.enterprise.unlimitedProjects'),
			t('planFeatures.enterprise.unlimitedResponses'),
			t('planFeatures.enterprise.unlimitedBugsAndFeatures'),
			t('planFeatures.enterprise.fullLiveChatHistory'),
			t('planFeatures.enterprise.removeBranding'),
			t('planFeatures.enterprise.webhookAccess'),
			t('planFeatures.enterprise.teamCollaboration'),
		],
	}
}

// Shared hook to fetch pricing data from Stripe
export const usePricingData = () => {
	return useQuery<PricingData, Error>({
		queryKey: ['pricing'],
		queryFn: async (): Promise<PricingData> => {
			const response = await fetch(`${API_URL}/api/stripe/pricing`)

			if (!response.ok) {
				throw new Error('Failed to fetch pricing data')
			}

			return response.json()
		},
		staleTime: 1000 * 60 * 30, // Cache for 30 minutes
	})
}

// Calculate savings percentage for yearly billing
export const calculateSavings = (
	monthlyPrice: number,
	yearlyPrice: number
): number => {
	if (typeof monthlyPrice === 'number' && typeof yearlyPrice === 'number') {
		const monthlyCostPerYear = monthlyPrice * 12
		const yearlyCost = yearlyPrice
		const savingsPercentage = Math.round(
			((monthlyCostPerYear - yearlyCost) / monthlyCostPerYear) * 100
		)
		return savingsPercentage > 0 ? savingsPercentage : 0
	}
	return 0
}

// Default plan structure builder
export const buildDefaultPlans = (
	t: (key: string) => string,
	planFeatures: ReturnType<typeof getPlanFeatures>
) => {
	const starterDefinition = PLAN_ENTITLEMENTS.starter
	const growthDefinition = PLAN_ENTITLEMENTS.growth

	return {
		free: {
			planId: 'free' as const,
			name: t('billingSettings.freePlanName'),
			monthlyPrice: 0,
			yearlyPrice: 0,
			monthlyPriceId: '',
			yearlyPriceId: '',
			features: planFeatures.free,
			additionalPrice: null,
			additionalMemberPriceMonthly: 0,
			additionalMemberPriceYearly: 0,
		},
		starter: {
			planId: 'starter' as const,
			name: t('pricing.starter.name'),
			monthlyPrice: starterDefinition.pricing.monthly,
			yearlyPrice: starterDefinition.pricing.yearly,
			monthlyPriceId: starterMonthlyPriceId,
			yearlyPriceId: starterYearlyPriceId,
			features: planFeatures.starter,
			additionalPrice: t('pricing.starter.additionalPrice') || null,
			additionalMemberPriceMonthly:
				starterDefinition.pricing.additionalMemberMonthly,
			additionalMemberPriceYearly:
				starterDefinition.pricing.additionalMemberYearly,
		},
		growth: {
			planId: 'growth' as const,
			name: t('pricing.growth.name'),
			monthlyPrice: growthDefinition.pricing.monthly,
			yearlyPrice: growthDefinition.pricing.yearly,
			monthlyPriceId: growthMonthlyPriceId,
			yearlyPriceId: growthYearlyPriceId,
			features: planFeatures.growth,
			additionalPrice: t('pricing.growth.additionalPrice') || null,
			additionalMemberPriceMonthly:
				growthDefinition.pricing.additionalMemberMonthly,
			additionalMemberPriceYearly:
				growthDefinition.pricing.additionalMemberYearly,
		},
		enterprise: {
			name: t('pricing.enterprise.name'),
			monthlyPrice: t('pricingCard.contactUsPlain') as const,
			yearlyPrice: t('pricingCard.contactUsPlain') as const,
			monthlyPriceId: '',
			yearlyPriceId: '',
			features: planFeatures.enterprise,
			additionalPrice: null,
		},
	}
}

// Merge API pricing data with default plans
export const mergePricingData = (
	defaultPlans: ReturnType<typeof buildDefaultPlans>,
	pricingData?: PricingData
) => {
	const plans = { ...defaultPlans }

	if (pricingData?.products?.length) {
		const apiFreePlan = pricingData.products.find((p) => p.planId === 'free')
		const apiStarterPlan = pricingData.products.find((p) => p.planId === 'starter')
		const apiGrowthPlan = pricingData.products.find((p) => p.planId === 'growth')

		if (apiFreePlan) {
			plans.free = {
				...plans.free,
				features: apiFreePlan.features.length
					? apiFreePlan.features
					: plans.free.features,
			}
		}

		if (apiStarterPlan) {
			plans.starter = {
				...plans.starter,
				monthlyPrice: apiStarterPlan.monthlyPrice || plans.starter.monthlyPrice,
				yearlyPrice: apiStarterPlan.yearlyPrice || plans.starter.yearlyPrice,
				monthlyPriceId:
					apiStarterPlan.monthlyPriceId || plans.starter.monthlyPriceId,
				yearlyPriceId:
					apiStarterPlan.yearlyPriceId || plans.starter.yearlyPriceId,
				additionalMemberPriceMonthly:
					apiStarterPlan.additionalMemberPriceMonthly ??
					plans.starter.additionalMemberPriceMonthly,
				additionalMemberPriceYearly:
					apiStarterPlan.additionalMemberPriceYearly ??
					plans.starter.additionalMemberPriceYearly,
				features: apiStarterPlan.features.length
					? apiStarterPlan.features
					: plans.starter.features,
			}
		}

		if (apiGrowthPlan) {
			plans.growth = {
				...plans.growth,
				monthlyPrice: apiGrowthPlan.monthlyPrice || plans.growth.monthlyPrice,
				yearlyPrice: apiGrowthPlan.yearlyPrice || plans.growth.yearlyPrice,
				monthlyPriceId:
					apiGrowthPlan.monthlyPriceId || plans.growth.monthlyPriceId,
				yearlyPriceId:
					apiGrowthPlan.yearlyPriceId || plans.growth.yearlyPriceId,
				additionalMemberPriceMonthly:
					apiGrowthPlan.additionalMemberPriceMonthly ??
					plans.growth.additionalMemberPriceMonthly,
				additionalMemberPriceYearly:
					apiGrowthPlan.additionalMemberPriceYearly ??
					plans.growth.additionalMemberPriceYearly,
				features: apiGrowthPlan.features.length
					? apiGrowthPlan.features
					: plans.growth.features,
			}
		}
	}

	return plans
}
